import { createElement } from "react";
import { IconBriefcase, IconChecklist, IconHome, IconListCheck, IconSchema, IconSettings, IconStar, IconUser } from "@tabler/icons-react";
import type { ReactNode } from "react";

/** Valid `to` values for authenticated header nav (must match route tree). */
export const AUTHED_NAV_LINKS = [
  "/dashboard",
  "/tasks",
  "/plans",
  "/assets",
  "/projects",
  "/rewards",
] as const;

export type AuthedNavTo = (typeof AUTHED_NAV_LINKS)[number];

export interface NavigationItem {
  icon: ReactNode;
  link: string;
  label: string;
  featureFlag?: string; // Optional feature flag requirement
}

export interface AuthenticatedNavigationItem
  extends Omit<NavigationItem, "link"> {
  link: AuthedNavTo;
}

export interface LogoConfig {
  src: string;
  alt: string;
  title: string;
}

export interface LegalLink {
  label: string;
  link: string;
}

export interface CTAButtons {
  login: { label: string; link: string };
  register: { label: string; link: string };
}

export interface NavigationConfig {
  logo: LogoConfig;
  authenticatedNav: ReadonlyArray<AuthenticatedNavigationItem>;
  legalLinks: Array<LegalLink>;
  ctaButtons: CTAButtons;
}

// Centralized navigation configuration
export const navigationConfig: NavigationConfig = {
  logo: {
    src: "/logo.png",
    alt: "Anchor Logo",
    title: "Anchor | Better home. Lower bills. Less stress.",
  },
  authenticatedNav: [
    {
      icon: createElement(IconHome, { size: 24 }),
      link: "/dashboard" as const,
      label: "Dashboard",
    },
    {
      icon: createElement(IconBriefcase, { size: 24 }),
      link: "/projects" as const,
      label: "Projects",
    },
    {
      icon: createElement(IconListCheck, { size: 24 }),
      link: "/tasks" as const,
      label: "Tasks",
    },
    {
      icon: createElement(IconChecklist, { size: 24 }),
      link: "/plans" as const,
      label: "Plans",
    },
    {
      icon: createElement(IconSchema, { size: 24 }),
      link: "/assets" as const,
      label: "Assets",
    },
    {
      icon: createElement(IconStar, { size: 24 }),
      link: "/rewards" as const,
      label: "Rewards",
    },
  ] as const satisfies ReadonlyArray<AuthenticatedNavigationItem>,
  legalLinks: [
    {
      label: "Privacy Policy",
      link: "/privacy",
    },
    {
      label: "Terms of Service",
      link: "/terms",
    },
    {
      label: "Support",
      link: "/support",
    },
  ],
  ctaButtons: {
    login: {
      label: "Sign In",
      link: "/sign-in",
    },
    register: {
      label: "Get Started",
      link: "/sign-up",
    },
  },
} as const;

// Helper functions for navigation
export const getAuthenticatedNavItems = (): Array<AuthenticatedNavigationItem> => {
  return [...navigationConfig.authenticatedNav];
};

export const getLegalLinks = (): Array<LegalLink> => {
  return navigationConfig.legalLinks;
};

export const getLogoConfig = (): LogoConfig => {
  return navigationConfig.logo;
};

export const getCTAButtons = (): CTAButtons => {
  return navigationConfig.ctaButtons;
};
